import React, { useState, useEffect, useMemo } from 'react';
import { FaRandom, FaTimes } from 'react-icons/fa';
import SiteWatermark from './SiteWatermark';
import '../Song.css';
import playlistItemsMap from '../assets/livestreaming/lean/playlist-items-lean.json';
import videosCatalog from '../assets/livestreaming/lean/videos-lean.json';

export default function PlaylistViewer({
  pageTitle = 'Media',
  playlists = [],
  showPageTitle = true,
}) {
  const [currentPlaylistIndex, setCurrentPlaylistIndex] = useState(() =>
    playlists.length ? 0 : null,
  );
  const [videoUrl, setVideoUrl] = useState(null);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [randomPlayNext, setRandomPlayNext] = useState(false);
  const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);

  const currentPlaylist =
    currentPlaylistIndex !== null && playlists[currentPlaylistIndex]
      ? playlists[currentPlaylistIndex]
      : null;

  const videoEntryMap = useMemo(() => {
    const map = {};
    Object.values(videosCatalog || {}).forEach((video) => {
      if (video?.id) {
        map[video.id] = video;
      }
    });
    return map;
  }, []);

  const playlistVideoIds = useMemo(() => {
    if (!currentPlaylist?.id) return [];
    const ids = playlistItemsMap?.[currentPlaylist.id];
    if (Array.isArray(ids)) return ids;
    return [];
  }, [currentPlaylist]);

  const playlistVideos = useMemo(() => {
    if (!playlistVideoIds.length) return [];
    return playlistVideoIds.map((id) => videoEntryMap[id]).filter(Boolean);
  }, [playlistVideoIds, videoEntryMap]);

  useEffect(() => {
    if (!currentPlaylist) {
      setVideoUrl(null);
      return;
    }
    const shuffleParam = randomPlayNext ? '&shuffle=1' : '';
    const safeIndex = Math.max(0, Math.min(selectedVideoIndex, Math.max(0, playlistVideoIds.length - 1)));
    const indexParam = Number.isFinite(safeIndex) ? `&index=${safeIndex}` : '';
    setVideoUrl(
      `https://www.youtube.com/embed/videoseries?list=${currentPlaylist.id}&autoplay=1&mute=0${shuffleParam}${indexParam}`,
    );
  }, [currentPlaylist, randomPlayNext, selectedVideoIndex, playlistVideoIds.length]);

  useEffect(() => {
    setSelectedVideoIndex(0);
  }, [currentPlaylist?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyToggle = (event) => {
      if (event.key === 'Control' && !event.repeat && !event.altKey && !event.metaKey) {
        setControlsOpen((prev) => !prev);
        return;
      }
      if (event.key === 'Escape' && controlsOpen) {
        setControlsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyToggle);
    return () => window.removeEventListener('keydown', handleKeyToggle);
  }, [controlsOpen]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const headerLogo = document.querySelector('.header-logo');
    if (!headerLogo) return undefined;
    const previousCursor = headerLogo.style.cursor;
    headerLogo.style.cursor = 'pointer';
    const handleHeaderClick = (event) => {
      event.preventDefault();
      setControlsOpen((prev) => !prev);
    };
    headerLogo.addEventListener('click', handleHeaderClick);
    return () => {
      headerLogo.removeEventListener('click', handleHeaderClick);
      headerLogo.style.cursor = previousCursor;
    };
  }, []);

  return (
    <div className="media-youtube-page">
      <SiteWatermark />
      <div className="playlist-topbar">
        {showPageTitle && (
          <div className="media-page-title" aria-live="polite">
            <span>{pageTitle}</span>
          </div>
        )}
        <div className="playlist-select">
          <select
            id="playlist-select"
            className="playlist-select-input"
            value={currentPlaylistIndex ?? ''}
            onChange={(event) => {
              const nextIndex = Number(event.target.value);
              if (Number.isFinite(nextIndex)) {
                setCurrentPlaylistIndex(nextIndex);
              }
            }}
          >
            {playlists.map((playlist, index) => (
              <option key={playlist.id} value={index}>
                {playlist.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className={`playlist-top-controls${controlsOpen ? ' open' : ''}`}>
        <div className="playlist-top-controls-inner">
          <div className="playlist-top-controls-header">
            <span>Playback controls</span>
            <button
              type="button"
              className="playlist-top-controls-close"
              onClick={() => setControlsOpen(false)}
              aria-label="Close controls"
            >
              <FaTimes />
            </button>
          </div>
          <div className="playlist-top-controls-actions">
            <button
              type="button"
              className={`playlist-control-button${randomPlayNext ? ' active' : ''}`}
              onClick={() => setRandomPlayNext((prev) => !prev)}
              aria-pressed={randomPlayNext}
              aria-label="Toggle random play"
            >
              <FaRandom />
              Random
            </button>
          </div>
        </div>
      </div>

      <section className="player-panel">
        {videoUrl ? (
          <iframe
            title={`${pageTitle} playlist`}
            src={videoUrl}
            frameBorder="0"
            allow="autoplay; encrypted-media"
            allowFullScreen
          />
        ) : (
          <div className="player-placeholder">
            <p>Loading playlist…</p>
          </div>
        )}
      </section>

      {playlistVideos.length > 0 && (
        <section className="playlist-mobile-tiles" aria-label={`${pageTitle} playlist videos`}>
          <div className="playlist-mobile-tiles__header">Playlist videos</div>
          <div className="playlist-mobile-tiles__list">
            {playlistVideos.map((video, index) => (
              <button
                key={`${video.id}-${index}`}
                type="button"
                className={`playlist-mobile-tiles__item${index === selectedVideoIndex ? ' is-active' : ''}`}
                onClick={() => setSelectedVideoIndex(index)}
              >
                <div
                  className="playlist-mobile-tiles__thumb"
                  style={{
                    backgroundImage: `url(${video.thumbnails?.medium?.url || video.thumbnails?.high?.url || video.thumbnails?.default?.url || ''})`,
                  }}
                />
                <div className="playlist-mobile-tiles__title">{video.title || 'Untitled'}</div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
